import { useEffect, useContext } from "react";
import { useSelector, useDispatch } from "react-redux";

import { getData } from "./store/dataSlice";
import { Route, Routes } from "react-router-dom";
import useDocumentTitle from "./utils/hooks/useDocumentTitle";

import Box from "@mui/material/Box";
import { ThemeProvider } from "@mui/material/styles";

import Alerts from "./components/UI/Alerts";
import DataStepper from "./components/DataStepper/DataStepper";
import ControlMenu from "./components/ControlMenu/ControlMenu";
import DataExplorerWrapper from "./components/DataExplorer/DataExplorerWrapper";
import DataEditor from "./components/DataExplorer/DataEditor";
import Faq from "./components/UI/Faq";
import Footer from "./components/UI/Footer";
import Header from "./components/UI/Header";
import Map from "./components/Map/Map";
import Theme from "./components/UI/Theme";
import { ViewStateContext } from "./components/UrlParser/UrlParser";
import ZoomControl from "./components/Map/ZoomControl";

import { updateUrl, useDidUpdate } from "./utils";
import { aoiCategories } from "./config/config.aoi";
import { metadataConfig } from "./config/config";
import { muiTheme } from "./config/config.theme";

import "./App.css";
import "./styles/react-tabs.css";
import "./styles/mapbox-gl.css";
import { getLayerGroups, parseLayerGroup } from "./store/layerGroupSlice";
import { layerGroupActions } from "./store";

function App() {
  const dispatch = useDispatch();
  useDocumentTitle(metadataConfig.title);

  // update URL when specific pieces of state change
  const aoiState = useSelector((state) => state.aoi);
  const layerState = useSelector((state) => state.layers);
  const uiState = useSelector((state) => state.ui);
  // const hivisCamerasState = useSelector((state) => state.data.hivisCameras);
  const dataState = useSelector((state) => state.data);
  const { viewState, setViewState } = useContext(ViewStateContext);

  // fetch any data that entire app depends on
  useEffect(() => {
    // populate control menu
    dispatch(getData({ dataId: "basins", source: "App" }));
    dispatch(getData({ dataId: "projects", source: "App" }));
    dispatch(getData({ dataId: "datasets", source: "App" }));
    // populate sites on map
    dispatch(getData({ dataId: "sites", source: "App" }));
    dispatch(
      getData({
        dataId: "nonNgwosSites",
        source: "App",
        columnsToFetch: dataState.nonNgwosSites.defaultColumns,
      })
    );
    aoiCategories.forEach((category) =>
      dispatch(getData({ dataId: category.data, source: "App" }))
    );

    getLayerGroups().then((r) => {
      const basins = r.data.basins;
      for (const basin of basins) {
        parseLayerGroup(basin).then((r) => {
          dispatch(layerGroupActions.addLayerGroup(r));
          dispatch(layerGroupActions.addBasinToAllIds(r.name));
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let aoiFetched = true;
  if (aoiState.selection.category) {
    aoiFetched =
      dataState[
        aoiCategories.find((cat) => cat.id === aoiState.selection.category).data
      ].fetched;
  }

  // custom hook identical to useEffect except ignores initial render
  useDidUpdate(() => {
    // TODO: detect if only mapState changed and don't update browser's history stack
    updateUrl({
      main: {
        aoiCategory: aoiState.selection.category,
        aoiSelectedIds: aoiState.selection.selectedIds,
        lat: viewState.latitude,
        lon: viewState.longitude,
        zoom: viewState.zoom,
        layerState,
        uiState: uiState,
      },
    });
  }, [aoiState, viewState, layerState]); // add state dependencies

  const baseRoute = process.env.REACT_APP_BASE_ROUTE
    ? process.env.REACT_APP_BASE_ROUTE
    : "";

  return (
    <ThemeProvider theme={muiTheme}>
      <div className="App" style={{ height: "100vh", overflow: "hidden" }}>
        <Theme />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          <Header baseRoute={baseRoute} />

          <Routes>
            <Route
              path={baseRoute + "/"}
              element={
                <Box
                  id="app-content"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    flex: "auto",
                    position: "relative",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-end",
                      justifyContent: "space-between",
                      zIndex: 3,
                      bottom: "25px",
                      right: "10px",
                      position: "absolute",
                    }}
                  >
                    <ZoomControl />
                  </Box>
                  <ControlMenu />
                  <Alerts />
                  <DataStepper />

                  <ViewStateContext.Provider
                    value={{ viewState, setViewState }}
                  >
                    {aoiFetched && <Map />}
                  </ViewStateContext.Provider>
                </Box>
              }
            />
            <Route
              path={baseRoute + "data-explorer"}
              element={
                <Box
                  id="app-content"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    flex: "auto",
                    maxHeight: "100%",
                    position: "relative",
                    overflowY: "auto",
                    backgroundColor: "primary.light",
                  }}
                >
                  <DataExplorerWrapper showFilters={true} />
                </Box>
              }
            />
            <Route
              path={baseRoute + "basin-coordination"}
              element={
                <Box
                  id="app-content"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    flex: "auto",
                    maxHeight: "100%",
                    position: "relative",
                    overflow: "hidden",
                    backgroundColor: "primary.light",
                  }}
                >
                  <DataEditor showFilters={false} />
                </Box>
              }
            />
          </Routes>
          <Faq />
          <Footer />
        </Box>
      </div>
    </ThemeProvider>
  );
}

export default App;
